import type { ProviderCapabilities } from '@postpilot/core/domain';
import { Monitor, Moon, Sun } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from '@/components/ui/primitives';
import { Skeleton } from '@/components/ui/states';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore, type Theme } from '@/stores/ui-store';

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);

  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  const [provider, setProvider] = React.useState<{
    active: string;
    displayName: string;
    capabilities: ProviderCapabilities;
  } | null>(null);

  React.useEffect(() => {
    void api.oauth
      .provider()
      .then(setProvider)
      .catch(() => setProvider(null));
  }, []);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Your account and how PostPilot behaves.
        </p>
      </div>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Appearance</CardTitle>
          <CardDescription>How PostPilot looks on this device.</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                { value: 'light', label: 'Light', icon: Sun },
                { value: 'dark', label: 'Dark', icon: Moon },
                { value: 'system', label: 'System', icon: Monitor },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                onClick={() => setTheme(option.value as Theme)}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-lg border p-4 transition-all',
                  theme === option.value
                    ? 'border-primary bg-accent shadow-sm'
                    : 'hover:border-primary/30 hover:bg-accent/50',
                )}
              >
                <option.icon className="size-4" />
                <span className="text-xs font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Provider — surfaced because it changes what the composer will accept,
          and because "why can't I attach an image" should have a visible answer. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Publishing</CardTitle>
          <CardDescription>The network PostPilot is currently posting to.</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {!provider ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <>
              <Row label="Provider" value={provider.displayName} />
              {provider.active === 'mock' && (
                <div className="border-status-processing/25 bg-status-processing/5 rounded-lg border p-3 text-xs">
                  <p className="text-status-processing font-medium">Demo mode</p>
                  <p className="text-muted-foreground mt-0.5">
                    Posts are simulated and never reach X. Set{' '}
                    <code className="bg-muted rounded px-1 py-0.5 font-mono">
                      SOCIAL_PROVIDER=x
                    </code>{' '}
                    with real credentials to publish for real.
                  </p>
                </div>
              )}

              <Separator />

              <Row
                label="Character limit"
                value={`${provider.capabilities.maxContentLength} (links count as 23)`}
              />
              <Row
                label="Images per post"
                value={
                  provider.capabilities.supportsMedia
                    ? String(provider.capabilities.maxMediaPerPost)
                    : 'Not supported'
                }
              />
              <Row
                label="Scheduling"
                value={
                  provider.capabilities.nativeScheduling
                    ? 'Handled by the provider'
                    : 'Handled by PostPilot’s scheduler'
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Account */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <Row label="Email" value={user?.email ?? '—'} />

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Sign out</p>
              <p className="text-muted-foreground text-xs">End this session on this device.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="truncate text-sm font-medium">{value}</span>
    </div>
  );
}
